import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BreakdownStateController } from '../../services/breakdown-state.js';
import { wsClient } from '../../services/websocket-client.js';
import type { BreakdownLevel, BreakdownHierarchyNode } from '../../services/breakdown-api.js';
import type { GoalBreakdown } from '../../services/morning-check-api.js';
import type { TerminalCreatedMessage } from '../../types/project.js';
import './cp-breakdown-terminal.js';
import './cp-breakdown-context.js';

type WorkflowPhase = 'loading' | 'rerun-warning' | 'chatting' | 'confirmed';

function getCurrentPeriod(level: BreakdownLevel): string {
  const now = new Date();
  if (level === 'monthly') {
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }
  if (level === 'weekly') {
    const oneJan = new Date(now.getFullYear(), 0, 1);
    const days = Math.floor((now.getTime() - oneJan.getTime()) / 86400000);
    const week = Math.ceil((days + oneJan.getDay() + 1) / 7);
    return `${now.getFullYear()}-W${String(week).padStart(2, '0')}`;
  }
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

const LEVEL_LABELS: Record<BreakdownLevel, string> = {
  monthly: 'Monthly Breakdown',
  weekly: 'Weekly Breakdown',
  daily: 'Daily Breakdown',
};

const PARENT_LEVEL: Record<BreakdownLevel, BreakdownLevel | null> = {
  monthly: null,
  weekly: 'monthly',
  daily: 'weekly',
};

@customElement('cp-breakdown-workflow')
export class CpBreakdownWorkflow extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: String }) breakdownType: BreakdownLevel = 'monthly';

  @state() private _phase: WorkflowPhase = 'loading';
  @state() private _rerunWarning = '';
  @state() private _sessionId = '';
  @state() private _sessionError = '';

  private _breakdown = new BreakdownStateController(this);
  private _period = '';
  private _wsHandler = this._onWsMessage.bind(this);
  private _sessionRequested = false;

  connectedCallback() {
    super.connectedCallback();
    this._period = getCurrentPeriod(this.breakdownType);
    wsClient.addEventListener('ws-message', this._wsHandler as EventListener);
    void this._initWorkflow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    wsClient.removeEventListener('ws-message', this._wsHandler as EventListener);
    this._destroySession();
  }

  protected updated(): void {
    // Retry session creation if not yet requested
    if (
      (this._phase === 'chatting' || this._phase === 'confirmed') &&
      !this._sessionId &&
      !this._sessionRequested
    ) {
      this._createWorkflowSession();
    }
  }

  private async _initWorkflow(): Promise<void> {
    this._phase = 'loading';

    await this._breakdown.loadHierarchy();
    await this._breakdown.loadByPeriod(this.breakdownType, this._period);

    if (this._breakdown.hasConversation) {
      const conv = this._breakdown.conversation!;
      if (conv.status === 'completed') {
        this._rerunWarning = `${LEVEL_LABELS[this.breakdownType]} fuer ${this._period} wurde bereits durchgefuehrt.`;
        this._phase = 'rerun-warning';
      } else if (conv.status === 'cancelled') {
        this._breakdown.reset();
        await this._breakdown.startConversation(this.breakdownType, this._period);
        this._phase = 'chatting';
        this._createWorkflowSession();
      } else if (conv.status === 'active' && this._breakdown.messages.length === 0) {
        this._breakdown.reset();
        await this._breakdown.startConversation(this.breakdownType, this._period);
        this._phase = 'chatting';
        this._createWorkflowSession();
      } else {
        this._phase = 'chatting';
        this._createWorkflowSession();
      }
    } else {
      await this._breakdown.startConversation(this.breakdownType, this._period);
      this._phase = 'chatting';
      this._createWorkflowSession();
    }
  }

  private _createWorkflowSession(): void {
    this._sessionRequested = true;
    wsClient.send({
      type: 'terminal:create-workflow',
      payload: { level: this.breakdownType },
      timestamp: new Date().toISOString(),
    });
  }

  private _destroySession(): void {
    if (this._sessionId) {
      wsClient.send({
        type: 'terminal:close',
        payload: { sessionId: this._sessionId },
        timestamp: new Date().toISOString(),
      });
      this._sessionId = '';
    }
  }

  private _onWsMessage(e: CustomEvent): void {
    const msg = e.detail as { type: string; payload: unknown };

    if (msg.type === 'terminal:created') {
      const payload = msg.payload as TerminalCreatedMessage;
      if (payload.sessionId.startsWith('workflow-')) {
        this._sessionId = payload.sessionId;
      }
    } else if (msg.type === 'terminal:workflow-complete') {
      const payload = msg.payload as { sessionId: string; level: string };
      if (payload.sessionId === this._sessionId) {
        this._phase = 'confirmed';
      }
    } else if (msg.type === 'terminal:session-end') {
      const payload = msg.payload as { sessionId: string; level: string; exitCode: number };
      if (payload.sessionId === this._sessionId) {
        if (payload.exitCode !== 0 && this._phase !== 'confirmed') {
          this._sessionError = 'Terminal-Session wurde unerwartet beendet.';
        }
        this._sessionId = '';
      }
    } else if (msg.type === 'terminal:exited') {
      const payload = msg.payload as { sessionId: string };
      if (payload.sessionId === this._sessionId) {
        this._sessionId = '';
      }
    }
  }

  private async _forceRerun(): Promise<void> {
    this._phase = 'loading';
    this._destroySession();
    this._sessionRequested = false;
    this._breakdown.reset();
    await this._breakdown.startConversation(this.breakdownType, this._period);
    this._phase = 'chatting';
    this._createWorkflowSession();
  }

  private _onCancel(): void {
    this._destroySession();
    this.dispatchEvent(new CustomEvent('workflow-close', {
      bubbles: true,
      composed: true,
    }));
  }

  private _getParentGoals(): GoalBreakdown[] {
    const parentLevel = PARENT_LEVEL[this.breakdownType];
    if (!parentLevel) return [];
    return this._flattenHierarchyLevel(this._breakdown.hierarchy, parentLevel);
  }

  private _flattenHierarchyLevel(
    nodes: BreakdownHierarchyNode[],
    level: BreakdownLevel,
  ): GoalBreakdown[] {
    const results: GoalBreakdown[] = [];
    for (const node of nodes) {
      if (node.level === level) {
        results.push({
          id: node.id,
          level: node.level,
          title: node.title,
          description: null,
          source_goal: null,
          project_assignment: null,
          priority: node.priority,
          status: node.status,
          parent_breakdown_id: null,
          created_at: '',
          updated_at: '',
        });
      }
      if (node.children.length > 0) {
        results.push(...this._flattenHierarchyLevel(node.children, level));
      }
    }
    return results;
  }

  private _renderLoading() {
    return html`
      <div class="cp-breakdown-workflow__loading">
        <p class="cp-breakdown-workflow__loading-text">Workflow wird geladen...</p>
      </div>
    `;
  }

  private _renderRerunWarning() {
    return html`
      <div class="cp-breakdown-workflow__rerun">
        <div class="cp-breakdown-workflow__rerun-card">
          <p class="cp-breakdown-workflow__rerun-text">${this._rerunWarning}</p>
          <div class="cp-breakdown-workflow__rerun-actions">
            <button
              class="cp-breakdown-workflow__rerun-btn cp-breakdown-workflow__rerun-btn--primary"
              @click=${this._forceRerun}
            >Trotzdem starten</button>
            <button
              class="cp-breakdown-workflow__rerun-btn cp-breakdown-workflow__rerun-btn--secondary"
              @click=${this._onCancel}
            >Abbrechen</button>
          </div>
        </div>
      </div>
    `;
  }

  private _renderTerminalWorkflow() {
    const parentGoals = this._getParentGoals();

    return html`
      <div class="cp-breakdown-workflow__layout ${parentGoals.length > 0 ? 'cp-breakdown-workflow__layout--with-context' : ''}">
        ${parentGoals.length > 0 ? html`
          <aside class="cp-breakdown-workflow__context-panel">
            <cp-breakdown-context .parentGoals=${parentGoals}></cp-breakdown-context>
          </aside>
        ` : nothing}
        <div class="cp-breakdown-workflow__chat-area">
          <cp-breakdown-terminal
            .sessionId=${this._sessionId}
            .breakdownLevel=${this.breakdownType}
          ></cp-breakdown-terminal>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <div class="cp-breakdown-workflow">
        ${this._breakdown.error || this._sessionError ? html`
          <div class="cp-breakdown-workflow__error">
            <p class="cp-breakdown-workflow__error-text">${this._breakdown.error || this._sessionError}</p>
            <button
              class="cp-breakdown-workflow__error-dismiss"
              @click=${() => { this._breakdown.clearError(); this._sessionError = ''; }}
            >Schliessen</button>
          </div>
        ` : nothing}
        ${this._phase === 'loading' ? this._renderLoading() : nothing}
        ${this._phase === 'rerun-warning' ? this._renderRerunWarning() : nothing}
        ${this._phase === 'chatting' || this._phase === 'confirmed' ? this._renderTerminalWorkflow() : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-breakdown-workflow': CpBreakdownWorkflow;
  }
}
